import posthog from 'posthog-js';

/**
 * Diagnostic context attached to a feedback submission (YAR-365).
 *
 * snake_case because these names travel unchanged from here to the wire to the
 * `public.feedback` columns. A camelCase type here would buy nothing but a
 * mapping layer to keep in sync.
 *
 * EVERY field is optional, and that is load-bearing: collecting context must
 * never be able to fail a submission.
 */
export interface FeedbackContext {
  route?: string;
  app_version?: string;
  user_agent?: string;
  viewport?: string;
  timezone?: string;
  posthog_distinct_id?: string;
  sentry_event_id?: string;
}

export interface BuildFeedbackContextInput {
  pathname: string;
  appVersion?: string;
  userAgent?: string;
  viewportWidth?: number;
  viewportHeight?: number;
  timezone?: string | null;
  posthogDistinctId?: string | null;
  sentryEventId?: string | null;
}

/**
 * Mirrored by the Edge Function, which re-clamps as the authority.
 *
 * Typed against `FeedbackContext` (not `as const`) so that adding a field to
 * the interface without adding its limit here fails `tsc -b`, the same
 * guard the edge's `CONTEXT_LIMITS` already has.
 */
const LIMITS: Record<keyof FeedbackContext, number> = {
  route: 200,
  app_version: 40,
  user_agent: 400,
  viewport: 20,
  timezone: 64,
  posthog_distinct_id: 200,
  sentry_event_id: 64,
};

/**
 * Trim, drop if empty, clamp if long. Returning `undefined` rather than `''`
 * matters: an empty string is stored and then reads back as a real value,
 * making "we could not collect this" indistinguishable from "this was blank".
 */
function clean(
  value: string | null | undefined,
  max: number,
): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed.slice(0, max);
}

/** Pure. All I/O happens in `collectFeedbackContext`. */
export function buildFeedbackContext(
  input: BuildFeedbackContextInput,
): FeedbackContext {
  const ctx: FeedbackContext = {};

  const route = clean(input.pathname, LIMITS.route);
  if (route) ctx.route = route;

  const appVersion = clean(input.appVersion, LIMITS.app_version);
  if (appVersion) ctx.app_version = appVersion;

  const userAgent = clean(input.userAgent, LIMITS.user_agent);
  if (userAgent) ctx.user_agent = userAgent;

  // Half a viewport is not a viewport -- "390x" tells you nothing.
  if (
    typeof input.viewportWidth === 'number' &&
    typeof input.viewportHeight === 'number'
  ) {
    const viewport = clean(
      `${input.viewportWidth}x${input.viewportHeight}`,
      LIMITS.viewport,
    );
    if (viewport) ctx.viewport = viewport;
  }

  const timezone = clean(input.timezone, LIMITS.timezone);
  if (timezone) ctx.timezone = timezone;

  const distinctId = clean(input.posthogDistinctId, LIMITS.posthog_distinct_id);
  if (distinctId) ctx.posthog_distinct_id = distinctId;

  const sentryEventId = clean(input.sentryEventId, LIMITS.sentry_event_id);
  if (sentryEventId) ctx.sentry_event_id = sentryEventId;

  return ctx;
}

/**
 * Reads the browser globals and PostHog, then delegates to the pure builder.
 *
 * Every read is individually guarded. `posthog.get_distinct_id()` does not
 * actually throw pre-init in this SDK version -- it short-circuits to
 * `undefined` via an optional chain -- but the try/catch is cheap insurance
 * against that changing in a future posthog-js version, and losing a
 * feedback report over an analytics library is not a trade worth making.
 */
export function collectFeedbackContext(
  pathname: string,
  timezone: string | null,
  sentryEventId?: string,
): FeedbackContext {
  let posthogDistinctId: string | undefined;
  try {
    posthogDistinctId = posthog.get_distinct_id();
  } catch {
    posthogDistinctId = undefined;
  }

  return buildFeedbackContext({
    pathname,
    appVersion: import.meta.env.VITE_GIT_SHA,
    userAgent:
      typeof navigator === 'undefined' ? undefined : navigator.userAgent,
    viewportWidth:
      typeof window === 'undefined' ? undefined : window.innerWidth,
    viewportHeight:
      typeof window === 'undefined' ? undefined : window.innerHeight,
    timezone,
    posthogDistinctId,
    sentryEventId,
  });
}
